<template>
  <div
    class="client"
    :class="summary['show'] && 'summary-view'"
    :style="`padding-bottom: ${isTemplate ? '132' : '88'}px`"
  >
    <pageTabHeader>
      <div class="title-row">
        <h5>{{ pageTitle }}</h5>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item @click="refreshClientFromQFD" v-if="showRefreshClient">
              <v-list-item-title>
                Refresh With Data From QFD
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="viewChangeHistory" v-if="!isTemplate">
              <v-list-item-title> View Change History </v-list-item-title>
            </v-list-item>
            <v-list-item v-if="nextEnvironment && !isTemplate && hasPromotionAccess">
              <v-list-item-title @click="promote"
                >Promote to {{ capitalizedNextEnvironment }}</v-list-item-title
              >
            </v-list-item>
            <v-list-item @click="discard" v-if="showDiscardChanges">
              <v-list-item-title> Discard Changes </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <tag v-if="!isLoading && environment.toUpperCase() !== 'PRODUCTION'" :title="promotionStatusDescription" :class="promotionStatusClass" class="promotion-tag">
          {{ promotionStatus }}
        </tag>
      </div>
      <pageTabs
        v-if="!summary['show']"
        @input="handleTabClick"
        @setTab="setTab"
      />
    </pageTabHeader>
    <tabContent :resetScroll="resetScroll">
      <loadingTab v-if="isLoading" />
      <div v-if="!isLoading && !summary['show']">
        <v-form v-model="isFormValid" ref="form">
          <configTab
            :key="id"
            :id="id"
            :tab="activeTab"
            :isTemplate="isTemplate"
            @updated="checkForFormRevalidation"
          />
        </v-form>
      </div>
      <changeSummary
        v-if="!isLoading && summary['show']"
        :id="id"
        @setTitle="setSummaryTitle"
        @setDetails="setSummaryDetails"
        @setIsFormValid="setIsSummaryFormValid"
      />
      <saveBar
        :id="id"
        :style="`right: ${rightPanelWidth}px`"
        :title="summary['title']"
        :details="summary['details']"
        :isTemplate="isTemplate"
        :isFormValid="isSaveBarActive"
        :isShowingChangeSummary="summary['show']"
        @showChangeSummary="showChangeSummary"
        @hideChangeSummary="hideChangeSummary"
      />
    </tabContent>
  </div>
</template>

<script>
/**
 * @fileoverview Client tab view
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { mapGetters } from "vuex";
import capitalize from "lodash/capitalize";
import configTab from "@/components/config-tab";
import pageTabHeader from "@/components/page-tab-header";
import pageTabs from "@/components/client/page-tabs";
import tabContent from "@/components/tab-content";
import changeSummary from "./change-summary";
import loadingTab from "./loadingTab";
import saveBar from "./saveBar";
import tabData from "./tabData";
import tag from "@/qux-common/qux22/components/molecules/tag";

export default {
  name: "Client",
  components: {
    configTab,
    pageTabHeader,
    pageTabs,
    tabContent,
    changeSummary,
    loadingTab,
    saveBar,
    tag
  },
  props: {
    id: {
      type: String,
    },
    title: {
      type: String,
      default: "",
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
    summary: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      tab: "Profile",
      resetScroll: undefined,
      tabData,
      isFormValid: false,
      revalidateFormOnNextUpdate: false,
    };
  },
  computed: {
    capitalizedNextEnvironment() {
      return capitalize(this.nextEnvironment);
    },
    hasPromotionAccess() {
      return this.accessRoles && this.accessRoles.includes("ConfigAdmin");
    },
    isLoading() {
      return (
        this.pendingCalls.includes("/client/template/retrieve") ||
        this.pendingCalls.includes("/client/retrieve")
      );
    },
    showRefreshClient() {
      //if we are an admin, lets allow this
      return this.accessRoles && this.accessRoles.includes("ConfigAdmin");
    },
    showDiscardChanges() {
      if(this.config[this.id] && this.config[this.id].Info){
        return !this.config[this.id].Info.IsPromoted;
      }
      return false;
    },
    pageTitle() {
      return (
        (this.id &&
          this.config[this.id] &&
          this.config[this.id].ClientInformation &&
          this.config[this.id].ClientInformation.LegalName) ||
        (this.config[this.id] &&
          this.config[this.id].Info &&
          this.config[this.id].Info.ClientId) ||
        this.title
      );
    },
    promotionStatus() {
      return ((this.config[this.id] &&
          this.config[this.id].Info &&
          this.config[this.id].Info.IsPromoted) ? "Promoted" : "Not Promoted"
      );
    },
    promotionStatusClass() {
      return ((this.config[this.id] &&
          this.config[this.id].Info &&
          this.config[this.id].Info.IsPromoted) ? "promotedTag" : "notPromotedTag"
      );
    },
    promotionStatusDescription() {
      return ((this.config[this.id] &&
          this.config[this.id].Info &&
          this.config[this.id].Info.IsPromoted) ? "These changes have been promoted" : "These changes have not been promoted."
      );
    },
    isSaveBarActive() {
      if (this.isTemplate) {
        // Templates don't need the full validation as all the normal config fields can be ignored when creating a template
        // Just need to make sure template name, ID, and label are filled out
        return !!(
          this.mergedConfig[this.id] &&
          this.mergedConfig[this.id].Info &&
          this.mergedConfig[this.id].Info.TemplateName &&
          this.mergedConfig[this.id].Info.ClientId &&
          this.mergedConfig[this.id].Info.ConfigLabel
        );
      }
      if (this.summary["show"]) {
        return this.summary["isFormValid"];
      }

      if (!this.activeTabData || !this.activeTabData.subTabValid) return false;
      const invalidTabs = Object.entries(this.activeTabData.subTabValid)
        .filter(([, value]) => !value)
        .map(([key]) => key);

      return !invalidTabs.length;
    },
    activeTab() {
      return tabData.find((tab) => tab.name === this.tab)["id"];
    },
    ...mapGetters({
      activeTabData: "getActiveTab",
      config: "getConfig",
      configTemplate: "getConfigTemplate",
      environment: "getEnvironment",
      nextEnvironment: "getNextEnvironment",
      pendingCalls: "getPendingCallList",
      rightPanelWidth: "getRightPanelWidth",
      accessRoles: "getRoles",
      mergedConfig: "getMergedConfig",
    }),
  },
  methods: {
    handleTabClick(tab) {
      this.resetScroll = tab;
    },
    refreshClientFromQFD() {
      this.$store.dispatch("retrieveConfigFromQFD", {
        ClientId: this.config[this.id].Info.ClientId,
        ClientInfoRuleId: this.id,
      });
    },
    viewChangeHistory() {
      this.$store.dispatch("openChangeHistory", {
        ClientId: this.config[this.id].Info.ClientId,
        ClientInfoRuleId: this.id,
      });
    },
    showChangeSummary() {
      this.$emit("showChangeSummary");
    },
    hideChangeSummary() {
      this.$emit("hideChangeSummary");
    },
    setSummaryTitle(title) {
      this.$emit("setSummaryTitle", title);
    },
    setSummaryDetails(details) {
      this.$emit("setSummaryDetails", details);
    },
    setIsSummaryFormValid(isFormValid) {
      this.$emit("setIsSummaryFormValid", isFormValid);
    },
    discard() {
      this.$store.dispatch("startDiscard", {
          clientId: this.config[this.id].Info.ClientId,
          clientInfoRuleId: this.config[this.id].Info.ClientInfoRuleId,
      });
    },
    promote() {
      this.$store.dispatch("startPromote", {
        clients: [
          {
            clientId: this.config[this.id].Info.ClientId,
            clientInfoRuleId: this.config[this.id].Info.ClientInfoRuleId,
          },
        ],
      });
    },
    setTab(tab) {
      this.tab = tab;
      if (this.activeTabData.subTabValid[this.activeTab] === false) {
        this.revalidateFormOnNextUpdate = true;
      }
    },
    checkForFormRevalidation() {
      // Since the tabs are re-rendered after switching, need to recheck the form validation for tabs
      // that have invalid form data so that the errors will display
      if (this.revalidateFormOnNextUpdate) {
        this.$refs.form.validate();
        this.revalidateFormOnNextUpdate = false;
      }
    },
  },
  watch: {
    isFormValid(value) {
      this.$store.dispatch("setSubtabValidity", {
        ClientInfoRuleId: this.id,
        tab: this.activeTab,
        value,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.summary-view {
  &.client {
    height: calc(100% - 26px);
  }
  .page-tab-header {
    padding-bottom: $qvo-spacing-24;
  }
}
.page-tab-header {
  flex-direction: column;
  padding-bottom: 0;
  .title-row {
    display: flex;
    align-items: center;
    gap: $qvo-spacing-4;
  }
}
.client {
  height: calc(100% - $page-tab-height);
  &::v-deep {
    .v-tab {
      text-transform: none;
    }
    .promotion-tag .v-chip__content .subtitle-2  {
      color: white;
    }
    .title-row .promotedTag {
      background-color: $qvo-color-branding-400;
    }
    .title-row .notPromotedTag {
      background-color: $qvo-color-yellow-100;
    }
  }
}
.save-bar {
  position: fixed;
  bottom: 0;
  z-index: 10;
  transition: right 200ms;
}
</style>